import React, { createContext, useState, useContext, useEffect } from 'react';
import { initialProducts } from '../data/initialProducts';
import {
  fetchAllProducts,
  addProductToFirebase,
  updateProductInFirebase,
  deleteProductFromFirebase,
  uploadImage
} from '../firebase/productService';

const ProductContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState(initialProducts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const fetchedProducts = await fetchAllProducts();
      setProducts(fetchedProducts);
      setError(null);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products. Using default products.');
      setProducts(initialProducts);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (category, product, subCategory = null) => {
    try {
      let imageUrl = product.image;
      
      // Handle image upload if there's a file
      if (product.imageFile) {
        imageUrl = await uploadImage(product.imageFile, product.name);
      }

      // Create product data WITHOUT imageFile
      const productData = {
        name: product.name,
        description: product.description,
        image: imageUrl,  // Only save the base64 string or URL
        sizes: product.sizes,
        featured: product.featured,
        category: category.charAt(0).toUpperCase() + category.slice(1),
        subCategory: subCategory
        // ❌ Do NOT include imageFile here!
      };

      // Add to Firebase
      const newProduct = await addProductToFirebase(category, productData, subCategory);

      // Update local state
      if (category === 'cakes' && subCategory) {
        setProducts(prev => ({
          ...prev,
          cakes: {
            ...prev.cakes,
            [subCategory]: [...prev.cakes[subCategory], newProduct]
          }
        }));
      } else {
        setProducts(prev => ({
          ...prev,
          [category]: [...prev[category], newProduct]
        }));
      }

      return newProduct;
    } catch (err) {
      console.error('Error adding product:', err);
      throw err;
    }
  };

  const updateProduct = async (category, productId, updatedData, subCategory = null) => {
    try {
      let imageUrl = updatedData.image;
      
      // Handle image upload if there's a new file
      if (updatedData.imageFile) {
        imageUrl = await uploadImage(updatedData.imageFile, updatedData.name);
      }

      // Create product data WITHOUT imageFile
      const productData = {
        name: updatedData.name,
        description: updatedData.description,
        image: imageUrl,
        sizes: updatedData.sizes,
        featured: updatedData.featured
        // ❌ Do NOT include imageFile here!
      };

      // Update in Firebase
      await updateProductInFirebase(category, productId, productData, subCategory);

      // Update local state
      if (category === 'cakes' && subCategory) {
        setProducts(prev => ({
          ...prev,
          cakes: {
            ...prev.cakes,
            [subCategory]: prev.cakes[subCategory].map(product =>
              product.id === productId ? { ...product, ...productData } : product
            )
          }
        }));
      } else {
        setProducts(prev => ({
          ...prev,
          [category]: prev[category].map(product =>
            product.id === productId ? { ...product, ...productData } : product
          )
        }));
      }
    } catch (err) {
      console.error('Error updating product:', err);
      throw err;
    }
  };

  const deleteProduct = async (category, productId, subCategory = null) => {
    try {
      await deleteProductFromFirebase(category, productId, subCategory);

      if (category === 'cakes' && subCategory) {
        setProducts(prev => ({
          ...prev,
          cakes: {
            ...prev.cakes,
            [subCategory]: prev.cakes[subCategory].filter(product => product.id !== productId)
          }
        }));
      } else {
        setProducts(prev => ({
          ...prev,
          [category]: prev[category].filter(product => product.id !== productId)
        }));
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      throw err;
    }
  };

  const resetProducts = async () => {
    setProducts(initialProducts);
    setError(null);
  };

  const value = {
    products,
    loading,
    error,
    addProduct,
    updateProduct,
    deleteProduct,
    resetProducts,
    refreshProducts: loadProducts
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};